import { useMemo, useState } from 'react';
import { createRoute, redirect, useNavigate } from '@tanstack/react-router';
import { Route as appRoute } from '../../app.js';
import { useAuthStore } from '../../../lib/auth.js';
import { useCreateQuote, type QuoteView } from '../../../hooks/useQuotes.js';
import type { PricingCourseRow } from '../../../hooks/usePricing.js';
import {
  buildCreateQuoteBody,
  canBuildQuotes,
  emptyClientForm,
  indicativeTotalCents,
  validateClientForm,
  QUOTE_IMMUTABLE_NOTE,
  type ClientFieldErrors,
  type ClientFormValues,
} from '../../../lib/quotes.js';
import { centsToAudDisplay } from '../../../lib/money.js';
import { ClientForm } from '../../../components/quotes/ClientForm.js';
import { CoursePicker } from '../../../components/quotes/CoursePicker.js';
import { QuoteLinesTable } from '../../../components/quotes/QuoteLinesTable.js';
import { Button } from '../../../components/ui/Button.js';
import { SendModalShell } from '../../../components/send/SendModalShell.js';

export const Route = createRoute({
  getParentRoute: () => appRoute,
  path: '/quotes/new',
  beforeLoad: () => {
    if (!canBuildQuotes(useAuthStore.getState().user)) throw redirect({ to: '/app' });
  },
  component: QuoteNewRoute,
});

/** The navigation seam — unexported, never rendered by a test. */
function QuoteNewRoute() {
  const navigate = useNavigate();
  return (
    <QuoteNewPage
      onCreated={(quote) => navigate({ to: '/app/quotes/$quoteId', params: { quoteId: quote.id } })}
      onCancel={() => navigate({ to: '/app/quotes' })}
    />
  );
}

/** Local "are you sure?" dialog — mirrors the four other copies (no shared component). */
function ConfirmDialog({
  open,
  title,
  body,
  detail,
  error,
  confirmLabel,
  loading,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  body: string;
  detail: string;
  error: string | null;
  confirmLabel: string;
  loading: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <SendModalShell open={open} onClose={onCancel} blocking={loading}>
      <div className="p-6">
        <h2 className="font-serif text-xl tracking-tight text-ink">{title}</h2>
        <p className="mt-2 text-sm text-muted">{body}</p>
        <p className="mt-2 text-sm text-ink">{detail}</p>
        {error && (
          <div className="mt-4 rounded-lg border border-danger/30 bg-danger-bg px-3 py-2 text-sm text-danger">
            {error}
          </div>
        )}
        <div className="mt-6 flex justify-end gap-2">
          <Button variant="secondary" onClick={onCancel} disabled={loading}>
            Cancel
          </Button>
          <Button variant="primary" loading={loading} onClick={onConfirm}>
            {confirmLabel}
          </Button>
        </div>
      </div>
    </SendModalShell>
  );
}

/**
 * Build a quote (Brief 7b). The ONLY writer in this feature.
 *
 * Order is deliberate: courses, then client, then the irreversible button — last,
 * after everything it will lock.
 *
 * The browser sends no money and no date: buildCreateQuoteBody is the only body
 * producer and its type cannot express either. Prices are re-resolved and locked
 * server-side at POST, which is why the running figure says "indicative".
 */
export function QuoteNewPage({
  onCreated,
  onCancel,
}: {
  onCreated: (quote: QuoteView) => void;
  onCancel: () => void;
}) {
  const [values, setValues] = useState<ClientFormValues>(emptyClientForm);
  const [picked, setPicked] = useState<PricingCourseRow[]>([]);
  const [errors, setErrors] = useState<ClientFieldErrors>({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const create = useCreateQuote();

  const pickedIds = useMemo(() => picked.map((p) => p.courseId), [picked]);
  const busy = create.isPending;

  function add(c: PricingCourseRow) {
    // Belt-and-braces with the picker's "Added" state: a duplicate courseId is a
    // 400, and it should be unreachable rather than merely unlikely.
    setPicked((prev) => (prev.some((p) => p.courseId === c.courseId) ? prev : [...prev, c]));
  }

  function requestIssue() {
    const next = validateClientForm(values);
    setErrors(next);
    if (Object.keys(next).length > 0 || picked.length === 0) return;
    setConfirmOpen(true);
  }

  function commit() {
    create.mutate(buildCreateQuoteBody(values, pickedIds), {
      onSuccess: ({ quote }) => {
        setConfirmOpen(false);
        onCreated(quote);
      },
      // No onError: create.error renders INSIDE the still-open dialog, so the
      // 409's own advice ("please try again") is actionable on the same button.
    });
  }

  return (
    <div className="max-w-5xl">
      <div className="mb-2 text-xs uppercase tracking-[0.14em] text-muted font-semibold">Quotes</div>
      <h1 className="font-serif text-4xl tracking-tighter2 mb-2">New quote</h1>
      <p className="text-muted text-sm mb-6">
        Pick the courses and enter the client&apos;s details. Prices are locked when you issue.
      </p>

      <h2 className="font-serif text-xl text-ink mb-2">Courses</h2>
      <CoursePicker pickedIds={pickedIds} onAdd={add} />

      <h2 className="font-serif text-xl text-ink mt-8 mb-2">On this quote</h2>
      <QuoteLinesTable
        lines={picked}
        disabled={busy}
        onRemove={(courseId) => setPicked((prev) => prev.filter((p) => p.courseId !== courseId))}
      />

      <h2 className="font-serif text-xl text-ink mt-8 mb-2">Client</h2>
      <ClientForm
        values={values}
        errors={errors}
        disabled={busy}
        onChange={(patch) => setValues((v) => ({ ...v, ...patch }))}
      />

      <div className="mt-6 flex items-center gap-2">
        <Button onClick={requestIssue} disabled={picked.length === 0 || busy}>
          Review &amp; issue quote
        </Button>
        <Button variant="secondary" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
      </div>
      {picked.length === 0 && <p className="mt-2 text-xs text-muted">Add at least one course.</p>}

      <ConfirmDialog
        open={confirmOpen}
        title="Issue this quote?"
        body={QUOTE_IMMUTABLE_NOTE}
        detail={`${picked.length} course${picked.length === 1 ? '' : 's'} for ${values.name.trim()}, totalling about ${centsToAudDisplay(
          indicativeTotalCents(picked),
        )}. Final prices are re-checked and locked by the server when you save.`}
        // Verbatim, never reworded: ApiError extends Error with the server's own
        // sentence, and both engine rejections already name the course and the
        // as-of date.
        error={create.error ? (create.error as Error).message : null}
        confirmLabel="Issue quote"
        loading={busy}
        onConfirm={commit}
        onCancel={() => setConfirmOpen(false)}
      />
    </div>
  );
}
